/**
 * Client rule model — draft factory, live validation, source-filter derivation,
 * rule_type derivation, plain-English summary, serialize/parse.
 *
 * The backend validator (saas-typescript) is authoritative; this mirrors it so
 * the wizard can gate "Next" and explain problems in real time. Everything is
 * driven by the /meta/schema capability matrix — no hardcoded keys/operators.
 */

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------

export const emptyDraft = () => ({
  name: "",
  description: "",
  event_type: null,
  condition: null,
  reward_mode: "one_time",
  cooldown_hours: null,
  milestone_interval: null,
  max_triggers_per_user: null,
  actions: [emptyAction("CREDIT")],
  valid_from: new Date().toISOString().slice(0, 10),
  valid_until: "",
  priority: 0,
});

export const emptyFactCheck = () => ({ type: "fact_check", key: "", operator: "", value: "" });

export const emptyHistorical = () => ({
  type: "historical_aggregate",
  tracking_key: "",
  tracking_value: "",
  aggregate_type: "count",
  operator: "gte",
  value: "",
  time_window_days: 365,
  source_type_filter: [],
});

export const emptyGroup = (type = "AND") => ({ type, children: [] });

export function emptyAction(type = "CREDIT") {
  if (type === "CREDIT") return { type: "CREDIT", amount: "", wallet_type: "default", description: "" };
  if (type === "CREDIT_DYNAMIC") return { type: "CREDIT_DYNAMIC", base_field: "", multiplier: "", wallet_type: "default", description: "" };
  return { type: "UPDATE_USER_SLAB", outcome_key: "", slab_name: "", slab_rank: "", description: "" };
}

// ---------------------------------------------------------------------------
// Matrix helpers
// ---------------------------------------------------------------------------

export const eventDef = (meta, eventType) =>
  (meta?.event_types || []).find((e) => e.name === eventType) || null;

export const factKeysForEvent = (meta, eventType) => {
  const ev = eventDef(meta, eventType);
  return (ev?.fact_check_keys || []).map((k) => ({ key: k, ...meta.fact_check_keys[k] }));
};

export const histKeysForEvent = (meta, eventType) => {
  const ev = eventDef(meta, eventType);
  return (ev?.historical_keys || []).map((k) => ({ key: k, ...meta.historical_keys[k] }));
};

export const aggregatesFor = (histDef) =>
  Array.isArray(histDef?.aggregate_type) ? histDef.aggregate_type : [histDef?.aggregate_type].filter(Boolean);

export const sourceTypesFor = (histDef, aggregate) => histDef?.source_types?.[aggregate] || [];

/** Auto-derive the default source_type_filter for (histKey, aggregate, event). */
export function deriveSourceTypeFilter(meta, eventType, histDef, aggregate) {
  const allowed = sourceTypesFor(histDef, aggregate);
  if (allowed.length <= 1) return [...allowed];
  const evSources = eventDef(meta, eventType)?.source_types || [];
  const match = allowed.filter((s) => evSources.includes(s));
  return match.length ? match : [...allowed];
}

export const deriveRuleType = (condition) => {
  if (!condition || typeof condition !== "object") return "fact_check";
  if (condition.type === "AND" || condition.type === "OR") return "compound";
  if (condition.type === "historical_aggregate") return "historical_aggregate";
  return "fact_check";
};

export function treeHasHistorical(condition) {
  if (!condition || typeof condition !== "object") return false;
  if (condition.type === "historical_aggregate") return true;
  if (condition.type === "AND" || condition.type === "OR") return (condition.children || []).some(treeHasHistorical);
  return false;
}

const ADVANCED_OPS = ["first_N_scans", "all_of", "active_streak"];
export const isAdvancedOp = (op) => ADVANCED_OPS.includes(op);

/**
 * Resolve the single scheme id a rule targets, from a metadata.scheme_id
 * condition (fact_check eq / in_list, or historical tracking_value). null when
 * no concrete scheme is chosen. Used to scope user_type / state / category to
 * that scheme's eligibility (a scheme pre-filters events for the rules engine).
 */
export function resolveSchemeId(condition) {
  let found = null;
  const walk = (c) => {
    if (!c || found) return;
    if (c.type === "fact_check" && (c.key === "metadata.scheme_id" || c.key === "metadata.scheme_ids")) {
      if (c.operator === "eq" && c.value !== "" && c.value !== "*") found = String(c.value);
      else if ((c.operator === "in_list" || c.operator === "not_in_list") && Array.isArray(c.value)) {
        const v = c.value.find((x) => x !== "*" && x !== "");
        if (v != null) found = String(v);
      }
    } else if (c.type === "historical_aggregate" && c.tracking_key === "metadata.scheme_id" && c.tracking_value && c.tracking_value !== "*") {
      found = String(c.tracking_value);
    }
    (c.children || []).forEach(walk);
  };
  walk(condition);
  return found;
}

/**
 * Resolve ALL scheme ids a rule targets (multi-scheme in_list conditions).
 * Returns a deduplicated string[] — empty when no scheme is pinned.
 * Unlike resolveSchemeId (scalar, stops at first), this walks the full tree.
 */
export function resolveSchemeIds(condition) {
  const found = new Set();
  const walk = (c) => {
    if (!c) return;
    if (c.type === "fact_check" && (c.key === "metadata.scheme_id" || c.key === "metadata.scheme_ids")) {
      if (c.operator === "eq" && c.value !== "" && c.value !== "*") {
        found.add(String(c.value));
      } else if ((c.operator === "in_list" || c.operator === "not_in_list") && Array.isArray(c.value)) {
        c.value.filter((x) => x !== "*" && x !== "").forEach((x) => found.add(String(x)));
      }
    } else if (c.type === "historical_aggregate" && c.tracking_key === "metadata.scheme_id" && c.tracking_value && c.tracking_value !== "*") {
      found.add(String(c.tracking_value));
    }
    (c.children || []).forEach(walk);
  };
  walk(condition);
  return [...found];
}

const lc = (x) => String(x).toLowerCase().trim();

// ---------------------------------------------------------------------------
// Validation (severity: 'error' blocks Next; 'warning' informs)
// ---------------------------------------------------------------------------

const err = (path, message) => ({ path, message, severity: "error" });
const warn = (path, message) => ({ path, message, severity: "warning" });

export function validateDraft(draft, meta, wallets, schemeFilters = null) {
  const errors = [];
  if (!meta) return errors;

  // Step 1
  if (!draft.name?.trim()) errors.push(err("name", "Rule name is required"));
  if (!draft.event_type) errors.push(err("event_type", "Choose an event to trigger on"));

  // Step 2
  if (!draft.condition) errors.push(err("condition", "Add at least one condition"));
  else validateCondition(draft.condition, draft, meta, "condition", errors, schemeFilters);

  // Step 3
  validateReward(draft, errors);

  // Step 4
  validateActions(draft, meta, wallets, errors);

  // Step 5
  if (!draft.valid_from) errors.push(err("valid_from", "Start date is required"));
  if (draft.valid_until && draft.valid_from && new Date(draft.valid_until) <= new Date(draft.valid_from))
    errors.push(err("valid_until", "End date must be after start date"));

  return errors;
}

function validateCondition(c, draft, meta, path, errors, schemeFilters) {
  if (!c || typeof c !== "object") return errors.push(err(path, "Invalid condition"));

  if (c.type === "AND" || c.type === "OR") {
    if (!c.children?.length) errors.push(err(`${path}.children`, "Add at least one condition to this group"));
    else if (c.children.length === 1) errors.push(warn(`${path}.children`, "Group has a single condition — consider flattening"));
    (c.children || []).forEach((child, i) => validateCondition(child, draft, meta, `${path}.children[${i}]`, errors, schemeFilters));
    return;
  }

  if (c.type === "fact_check") return validateFactCheck(c, draft, meta, path, errors, schemeFilters);
  if (c.type === "historical_aggregate") return validateHistorical(c, draft, meta, path, errors);
  errors.push(err(`${path}.type`, "Select a condition type"));
}

function validateFactCheck(c, draft, meta, path, errors, schemeFilters) {
  const def = c.key && meta.fact_check_keys[c.key];
  if (!c.key) errors.push(err(`${path}.key`, "Select a field"));
  else if (!def) errors.push(err(`${path}.key`, `Unknown field "${c.key}"`));
  else if (draft.event_type && !def.event_types.includes(draft.event_type))
    errors.push(err(`${path}.key`, `"${def.label}" is not available on ${draft.event_type} events`));

  if (!c.operator) errors.push(err(`${path}.operator`, "Select an operator"));
  else if (def && !def.operators.includes(c.operator))
    errors.push(err(`${path}.operator`, `"${c.operator}" isn't allowed for ${def.label}`));

  const isList = c.operator === "in_list" || c.operator === "not_in_list";
  if (c.value === "" || c.value === undefined || c.value === null) errors.push(err(`${path}.value`, "Value is required"));
  else if (isList && (!Array.isArray(c.value) || c.value.length === 0)) errors.push(err(`${path}.value`, "Provide at least one value"));
  else if (def?.type === "number" && !isList && isNaN(Number(c.value))) errors.push(err(`${path}.value`, "Value must be a number"));

  // Scheme-scope: a scheme pre-filters events by user type / state / product,
  // so a rule value outside the scheme's eligibility can never match.
  if (schemeFilters && def && c.value !== "" && c.value != null) {
    const vals = (Array.isArray(c.value) ? c.value : [c.value]).filter((v) => v !== "*" && v !== "");
    const outside = (allowed) => {
      const set = new Set((allowed || []).map(lc));
      return set.size ? vals.filter((v) => !set.has(lc(v))) : [];
    };
    if (c.key === "user_type") {
      const bad = outside((schemeFilters.user_types || []).map((u) => u.name));
      if (bad.length) errors.push(err(`${path}.value`, `This scheme doesn't include user type(s): ${bad.join(", ")} — the rule can't match`));
    } else if (c.key === "user_state") {
      const bad = outside(schemeFilters.states);
      if (bad.length) errors.push(err(`${path}.value`, `This scheme doesn't apply in: ${bad.join(", ")}`));
    } else if (c.key === "category") {
      const bad = outside(schemeFilters.product_categories);
      if (bad.length) errors.push(err(`${path}.value`, `This scheme has no products in category: ${bad.join(", ")}`));
    } else if (c.key === "product_code") {
      errors.push(warn(`${path}.value`, "Confirm this product is part of the scheme"));
    }
  }
}

function validateHistorical(c, draft, meta, path, errors) {
  const def = c.tracking_key && meta.historical_keys[c.tracking_key];
  if (!c.tracking_key) errors.push(err(`${path}.tracking_key`, "Select what to track"));
  else if (!def) errors.push(err(`${path}.tracking_key`, `Unknown tracking key "${c.tracking_key}"`));
  else if (draft.event_type && !def.event_types.includes(draft.event_type))
    errors.push(err(`${path}.tracking_key`, `"${def.label}" is not produced by ${draft.event_type} events`));

  const aggregates = def ? aggregatesFor(def) : [];
  const aggregate = c.aggregate_type;
  if (def && aggregates.length > 1 && !aggregates.includes(aggregate))
    errors.push(err(`${path}.aggregate_type`, `Choose aggregate (${aggregates.join(" or ")})`));

  const advanced = isAdvancedOp(c.operator);
  if (!c.operator) errors.push(err(`${path}.operator`, "Select an operator"));

  if (c.operator === "all_of") {
    if (!Array.isArray(c.value) || c.value.length === 0) errors.push(err(`${path}.value`, "List the items to complete"));
    if (c.tracking_value !== "*") errors.push(err(`${path}.tracking_value`, "‘Complete all of’ must use ‘*’"));
  } else {
    if (c.value === "" || isNaN(Number(c.value))) errors.push(err(`${path}.value`, "Value must be a number"));
    else if ((c.operator === "first_N_scans" || c.operator === "active_streak") && Number(c.value) <= 0)
      errors.push(err(`${path}.value`, "Value must be greater than 0"));
    if (!c.tracking_value && c.tracking_value !== 0) errors.push(err(`${path}.tracking_value`, "A tracking value is required"));
    else if ((c.operator === "first_N_scans" || c.operator === "active_streak") && c.tracking_value === "*")
      errors.push(err(`${path}.tracking_value`, "This operator needs a specific value, not ‘*’"));
  }

  if (!advanced && (!c.time_window_days || c.time_window_days <= 0))
    errors.push(err(`${path}.time_window_days`, "Time window (days) must be positive"));

  if (!Array.isArray(c.source_type_filter) || c.source_type_filter.length === 0)
    errors.push(err(`${path}.source_type_filter`, "Source type is required"));
  else if (def && aggregate) {
    const allowed = sourceTypesFor(def, aggregate);
    const bad = c.source_type_filter.filter((s) => !allowed.includes(s));
    if (bad.length) errors.push(err(`${path}.source_type_filter`, `Invalid source(s): ${bad.join(", ")}`));
  }
}

function validateReward(draft, errors) {
  if (draft.reward_mode === "milestone") {
    if (!draft.milestone_interval || draft.milestone_interval <= 0)
      errors.push(err("milestone_interval", "Milestone interval is required"));
    if (!treeHasHistorical(draft.condition))
      errors.push(err("reward_mode", "Milestone mode needs a history check to supply the running count"));
  }
  if (draft.reward_mode === "repeating" && draft.cooldown_hours != null && draft.cooldown_hours < 0)
    errors.push(err("cooldown_hours", "Cooldown must be zero or positive"));
  if (draft.max_triggers_per_user != null && draft.max_triggers_per_user !== "" && Number(draft.max_triggers_per_user) <= 0)
    errors.push(err("max_triggers_per_user", "Max triggers must be positive"));
}

function validateActions(draft, meta, wallets, errors) {
  const actions = draft.actions || [];
  if (!actions.length) return errors.push(err("actions", "Add at least one action"));

  const seenType = new Set();
  const seenWallet = new Set();
  const seenOutcome = new Set();
  const evDef = eventDef(meta, draft.event_type);

  actions.forEach((a, i) => {
    const p = `actions[${i}]`;
    if (seenType.has(a.type)) errors.push(err(p, `Only one "${a.type}" action allowed per rule`));
    seenType.add(a.type);

    if (a.type === "CREDIT") {
      if (a.amount === "" || Number(a.amount) <= 0) errors.push(err(`${p}.amount`, "Amount must be greater than 0"));
      validateWallet(a.wallet_type, `${p}.wallet_type`, seenWallet, errors, wallets);
      if (!a.description?.trim()) errors.push(err(`${p}.description`, "Description is required"));
    } else if (a.type === "CREDIT_DYNAMIC") {
      const allowed = evDef?.credit_base_fields || [];
      if (!a.base_field) errors.push(err(`${p}.base_field`, "Select a base field"));
      else if (!allowed.includes(a.base_field)) errors.push(err(`${p}.base_field`, `Not a numeric field on ${draft.event_type} events`));
      if (a.multiplier === "" || Number(a.multiplier) <= 0) errors.push(err(`${p}.multiplier`, "Multiplier must be greater than 0"));
      validateWallet(a.wallet_type, `${p}.wallet_type`, seenWallet, errors, wallets);
      if (!a.description?.trim()) errors.push(err(`${p}.description`, "Description is required"));
    } else if (a.type === "UPDATE_USER_SLAB") {
      if (!a.outcome_key?.trim()) errors.push(err(`${p}.outcome_key`, "Outcome key is required"));
      else if (seenOutcome.has(a.outcome_key)) errors.push(err(`${p}.outcome_key`, "Duplicate outcome_key"));
      seenOutcome.add(a.outcome_key);
      if (!a.slab_name?.trim()) errors.push(err(`${p}.slab_name`, "Slab name is required"));
      if (a.slab_rank === "" || isNaN(Number(a.slab_rank))) errors.push(err(`${p}.slab_rank`, "Slab rank must be a number"));
      if (!a.description?.trim()) errors.push(err(`${p}.description`, "Description is required"));
    }

    // scheme-wallet vs base-scan coherence (warn)
    if ((a.type === "CREDIT" || a.type === "CREDIT_DYNAMIC") && a.wallet_type && a.wallet_type !== "default" && draft.event_type === "scan")
      errors.push(warn(`${p}.wallet_type`, "Crediting a scheme wallet from a base scan is unusual"));
  });
}

function validateWallet(walletType, path, seenWallet, errors, wallets) {
  if (walletType === "" || walletType == null) return errors.push(err(path, "Choose a wallet"));
  if (walletType !== "default" && isNaN(Number(walletType))) return errors.push(err(path, "Invalid wallet"));
  if (seenWallet.has(String(walletType))) errors.push(err(path, "Another credit already targets this wallet"));
  seenWallet.add(String(walletType));

  if (walletType !== "default" && Array.isArray(wallets) && wallets.length > 0) {
    const exists = wallets.some((w) => String(w.scheme_id) === String(walletType));
    if (!exists) {
      errors.push(err(path, `Target scheme wallet (ID: ${walletType}) no longer exists or was deleted`));
    }
  }
}

// Which step a validation path belongs to (for the step rail chips).
export function stepOfPath(path) {
  if (path.startsWith("name") || path.startsWith("event_type")) return 1;
  if (path.startsWith("condition")) return 2;
  if (path.startsWith("reward_mode") || path.startsWith("milestone") || path.startsWith("cooldown") || path.startsWith("max_triggers")) return 3;
  if (path.startsWith("actions")) return 4;
  if (path.startsWith("valid")) return 5;
  return 5;
}

// ---------------------------------------------------------------------------
// Serialize / parse
// ---------------------------------------------------------------------------

export function serialize(draft, status) {
  const clean = pruneCondition(draft.condition);
  const body = {
    name: draft.name.trim(),
    description: draft.description || null,
    event_type: draft.event_type,
    rule_type: deriveRuleType(clean),
    priority: Number(draft.priority) || 0,
    status,
    reward_mode: draft.reward_mode,
    milestone_interval: draft.reward_mode === "milestone" ? Number(draft.milestone_interval) : null,
    cooldown_hours: draft.reward_mode === "repeating" && draft.cooldown_hours != null && draft.cooldown_hours !== "" ? Number(draft.cooldown_hours) : null,
    max_triggers_per_user: draft.max_triggers_per_user ? Number(draft.max_triggers_per_user) : null,
    valid_from: new Date(draft.valid_from).toISOString(),
    valid_until: draft.valid_until ? new Date(draft.valid_until).toISOString() : null,
    condition: clean,
    actions: (draft.actions || []).map(serializeAction),
  };
  return body;
}

function serializeAction(a) {
  if (a.type === "CREDIT") return { type: "CREDIT", amount: Number(a.amount), wallet_type: String(a.wallet_type), description: a.description };
  if (a.type === "CREDIT_DYNAMIC") return { type: "CREDIT_DYNAMIC", base_field: a.base_field, multiplier: Number(a.multiplier), wallet_type: String(a.wallet_type), description: a.description };
  return { type: "UPDATE_USER_SLAB", outcome_key: a.outcome_key, slab_name: a.slab_name, slab_rank: Number(a.slab_rank), description: a.description };
}

// Order children so cheap fact-checks (gates) come first, then nested groups,
// then historical aggregates (DB). The engine evaluates AND/OR in order and
// short-circuits (rulesEngineService: first mismatch/match wins), so this skips
// the historical query when a fact-check gate already breaks the rule. AND/OR
// are commutative — the boolean result is unchanged, only short-circuit/perf.
const CHILD_RANK = (n) => (n.type === "fact_check" ? 0 : n.type === "AND" || n.type === "OR" ? 1 : 2);

function pruneCondition(c) {
  if (!c) return c;
  if (c.type === "AND" || c.type === "OR") {
    const children = (c.children || []).map(pruneCondition);
    children.sort((a, b) => CHILD_RANK(a) - CHILD_RANK(b));
    return { type: c.type, children };
  }
  if (c.type === "fact_check") return { type: "fact_check", key: c.key, operator: c.operator, value: coerceValue(c.value) };
  if (c.type === "historical_aggregate") {
    const out = {
      type: "historical_aggregate",
      tracking_key: c.tracking_key,
      tracking_value: c.tracking_value,
      aggregate_type: c.aggregate_type,
      operator: c.operator,
      value: c.operator === "all_of" ? c.value : coerceValue(c.value),
      source_type_filter: c.source_type_filter,
    };
    if (!isAdvancedOp(c.operator)) out.time_window_days = Number(c.time_window_days);
    if (c.reset_period_days) out.reset_period_days = Number(c.reset_period_days);
    if (c.operator === "all_of" && c.min_scans_per_item) out.min_scans_per_item = Number(c.min_scans_per_item);
    return out;
  }
  return c;
}

const coerceValue = (v) => (Array.isArray(v) ? v : v !== "" && !isNaN(Number(v)) ? Number(v) : v);

/** Build a draft from an API rule (edit / round-trip). */
export function fromRule(r) {
  const actions = Array.isArray(r.action) ? r.action : r.actions_json || (r.action ? [r.action] : []);
  return {
    name: r.name || "",
    description: r.description || "",
    event_type: r.event_type || null,
    condition: r.condition || r.conditions_json || null,
    reward_mode: r.reward_mode || "one_time",
    cooldown_hours: r.cooldown_hours ?? (r.cooldown_seconds ? Math.round(r.cooldown_seconds / 3600) : null),
    milestone_interval: r.milestone_interval ?? null,
    max_triggers_per_user: r.max_triggers_per_user ?? null,
    actions: actions.map((a) => ({ ...emptyAction(a.type), ...a })),
    valid_from: (r.valid_from || r.start_date || "").slice(0, 10),
    valid_until: (r.valid_until || "").slice(0, 10),
    priority: r.priority ?? 0,
  };
}
